/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { AuditApiService } from 'src/app/shared/services/customer-api/audit-api.service';
import { NewsFeedProcessorService } from './news-feed-processor.service';

@Injectable({
  providedIn: 'root',
})
export class NewsFeedTypeHandlerService {
  constructor(
    private auditApi: AuditApiService,
    private routing: RoutingService,
    private processor: NewsFeedProcessorService,
  ) {}

  /**
   * Process stories type newsfeed
   */
  processStoriesNewsfeed(result: any): any {
    const processedData = {
      newsfeed: [],
      morenews: [],
      loading: false,
    };

    if (!result.stories || !Array.isArray(result.stories)) {
      return processedData;
    }

    result.stories.forEach((story: any) => {
      if (story.main_article && story.articles?.length > 0) {
        processedData.newsfeed.push({
          story: story.main_article.title,
          article: story.main_article,
          news: [],
          size: story.articles.length,
          id: story.story_id,
        });
        processedData.morenews.push(false);
      }
    });

    // Format titles
    processedData.newsfeed.forEach((story: any) =>
      this.processor.formatNewsTitle(story.article, {}),
    );

    return processedData;
  }

  /**
   * Process newsaudit type newsfeed (non-flagged, non-dismissed)
   */
  processNewsAuditNewsfeed(result: any, params: any): Observable<any> {
    return forkJoin([
      this.auditApi.getAuditedIds(),
      this.auditApi.getDismissedIds(),
    ]).pipe(
      map(([auditedResult, dismissedResult]) => {
        const auditedIds = this.extractIds(auditedResult);
        const dismissedIds = this.extractIds(dismissedResult);
        const allIds = new Set([...auditedIds, ...dismissedIds]);

        const processedData = {
          newsfeed: [],
          newsids: [],
          auditedIds,
          dismissedIds,
          loading: false,
          scrollnext: 'null',
          scrollIndex: 0,
          readyscroll: false,
        };

        if (!result?.news?.length) {
          // Ensure no scrolling indicators for empty results
          processedData.readyscroll = false;
          processedData.scrollnext = 'null';
          processedData.scrollIndex = 0;
          return processedData;
        }

        result.news.forEach((news: any) => {
          if (!allIds.has(news.id)) {
            this.processNewsAuditItem(news, params, processedData);
          }
        });

        // Post-process the newsfeed
        this.postProcessNewsfeed(processedData.newsfeed, params);

        // Handle scroll settings for specific types
        // Special handling for Bookmarked, Flagged, and Dismissed - they should never use from_index
        if (
          params.newstype === 'Bookmarked' ||
          params.newstype === 'Flagged' ||
          params.newstype === 'Dismissed'
        ) {
          processedData.scrollIndex = 0;
          processedData.scrollnext = 'null';
          processedData.readyscroll = false;
        } else if (result.from_index !== undefined && result.from_index !== 0) {
          processedData.scrollIndex = 0;
          processedData.scrollnext = result.from_index;
          processedData.readyscroll = true;
        } else if (
          params.newstype === 'Relevant' ||
          params.newstype === 'Chronological'
        ) {
          processedData.scrollIndex = 0;
          processedData.scrollnext = 'null';
          processedData.readyscroll = false;
        }

        return processedData;
      }),
    );
  }

  /**
   * Process flagged newsaudit items
   */
  processFlaggedNewsfeed(result: any, params: any): Observable<any> {
    return this.auditApi.getAuditedCases({ num_items: 1000000 }).pipe(
      map((auditCases) => {
        const processedData = {
          newsfeed: [],
          newsids: [],
          loading: false,
          scrollnext: 'null',
          scrollIndex: 50,
          readyscroll: true,
        };

        if (!result.news.length) {
          return processedData;
        }

        result.news.forEach((news: any) => {
          const auditInfoFull = auditCases.hits.filter(
            (item: any) => item.article_id === news.id,
          );

          if (auditInfoFull.length > 0) {
            this.processFlaggedNewsItem(
              news,
              auditInfoFull,
              params,
              processedData,
            );
          }
        });

        this.postProcessNewsfeed(processedData.newsfeed, params);
        return processedData;
      }),
    );
  }

  /**
   * Process dismissed newsaudit items
   */
  processDismissedNewsfeed(result: any, params: any): Observable<any> {
    return this.auditApi.getDismissedCases({ num_items: 1000000 }).pipe(
      map((dismissedCases) => {
        const processedData = {
          newsfeed: [],
          newsids: [],
          loading: false,
          scrollnext: 'null',
          scrollIndex: 50,
          readyscroll: true,
        };

        if (!result.news.length) {
          return processedData;
        }

        result.news.forEach((news: any) => {
          const auditInfoFull = dismissedCases.hits.filter(
            (item: any) => item.article_id === news.id,
          );

          if (auditInfoFull.length > 0) {
            this.processDismissedNewsItem(
              news,
              auditInfoFull,
              params,
              processedData,
            );
          }
        });

        this.postProcessNewsfeed(processedData.newsfeed, params);
        return processedData;
      }),
    );
  }

  /**
   * Process standard newsfeed (chronological, events, etc.)
   */
  processStandardNewsfeed(result: any, params: any, newstype: string): any {
    const processedData = {
      newsfeed: [],
      newsids: [],
      loading: false,
      scrollnext: 'null',
      scrollIndex: 0,
      readyscroll: false,
    };

    if (!result.news?.length) {
      // Ensure no scrolling indicators for empty results
      processedData.readyscroll = false;
      processedData.scrollnext = 'null';
      processedData.scrollIndex = 0;
      return processedData;
    }

    result.news.forEach((news: any) => {
      this.processStandardNewsItem(news, params, processedData);
    });

    this.postProcessNewsfeed(processedData.newsfeed, params);

    if (
      newstype === 'Bookmarked' ||
      newstype === 'Flagged' ||
      newstype === 'Dismissed'
    ) {
      processedData.scrollIndex = 0;
      processedData.scrollnext = 'null';
      processedData.readyscroll = false;
    } else if (result.from_index !== undefined && result.from_index !== 0) {
      processedData.scrollIndex = 0;
      processedData.scrollnext = result.from_index;
      processedData.readyscroll = true;
    } else if (newstype === 'Relevant' || newstype === 'Chronological') {
      processedData.scrollIndex = 0;
      processedData.scrollnext = 'null';
      processedData.readyscroll = false;
    } else {
      processedData.scrollIndex = newstype !== 'Events' ? 50 : 1000;
      processedData.scrollnext = 'null';
      processedData.readyscroll = processedData.scrollIndex !== 0;
    }

    return processedData;
  }

  /**
   * Extract IDs from audit results
   */
  private extractIds(result: any): string[] {
    const ids: string[] = [];
    for (const prop of Object.keys(result)) {
      ids.push(...result[prop]);
    }
    return ids;
  }

  /**
   * Process a single news audit item
   */
  private processNewsAuditItem(
    news: any,
    params: any,
    processedData: any,
  ): void {
    this.processor.processAuditData(news);
    processedData.newsids.push(news.id);

    if (!news.title) {
      news.title = news.content;
    }

    this.processor.calculateSentimentProportions(news, params);
    processedData.newsfeed.push(news);
  }

  /**
   * Process a single flagged news item
   */
  private processFlaggedNewsItem(
    news: any,
    auditInfoFull: any[],
    params: any,
    processedData: any,
  ): void {
    const auditInfo = auditInfoFull[0];
    news['resolved'] =
      auditInfo.resolve_timestamp !== null ? 'Resolved' : 'Opened';
    news['timestamp'] =
      auditInfo.resolve_timestamp !== null
        ? auditInfo.resolve_timestamp
        : auditInfo.open_timestamp;
    news['subjects'] = auditInfoFull.map((element: any) => ({
      subject: element.subject,
      id: element.subject_id,
    }));
    news['auditInfo'] = auditInfo;

    processedData.newsids.push(news.id);

    if (!news.title) {
      news.title = news.content;
    }

    this.processor.calculateSentimentProportions(news, params);
    processedData.newsfeed.push(news);
  }

  /**
   * Process a single dismissed news item
   */
  private processDismissedNewsItem(
    news: any,
    auditInfoFull: any[],
    params: any,
    processedData: any,
  ): void {
    const auditInfo = auditInfoFull[0];
    news['timestamp'] = auditInfo.timestamp;
    news['subjects'] = auditInfoFull.map((element: any) => ({
      subject: element.subject,
      id: element.subject_id,
    }));
    news['auditInfo'] = auditInfo;

    processedData.newsids.push(news.id);

    if (!news.title) {
      news.title = news.content;
    }

    this.processor.calculateSentimentProportions(news, params);
    processedData.newsfeed.push(news);
  }

  /**
   * Process a standard news item
   */
  private processStandardNewsItem(
    news: any,
    params: any,
    processedData: any,
  ): void {
    if (this.routing.isFolio() || this.routing.isHomepage()) {
      this.processor.processAuditData(news);
    }

    processedData.newsids.push(news.id);

    if (!news.title) {
      news.title = news.content;
    }

    this.processor.calculateSentimentProportions(news, params);
    processedData.newsfeed.push(news);
  }

  /**
   * Apply post-processing to newsfeed
   */
  private postProcessNewsfeed(newsfeed: any[], params: any): void {
    const frametime = {
      today: true,
      twentyfourhours: true,
      week: true,
      month: true,
      quarter: true,
      year: true,
    };

    this.processor.sortNewsfeed(newsfeed, params.newstype);

    newsfeed.forEach((news) => {
      this.processor.addTimeFrameLabels(news, params, frametime);
      this.processor.formatNewsTitle(news, params);
    });
  }
}
